import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useEffect} from 'react';

import TransitionFieldInfoSection from 'processes/modals/field-mapping/transition-modal-with-flowchart-editor/TransitionFieldInfoSection';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import FlowchartSettingsPane from './DefaultTaskSchemaNodeFlowchartSettingsPane';
import {NodeDetailsSidepanelContentProps} from './ProcessFlowchartNodeAdapter';

export enum NodeSettingsTabKeys {
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
  NODE_FIELD_DETAILS = 'NODE_FIELD_DETAILS',
}

function NodeFieldDetailsPane(props: NodeDetailsSidepanelContentProps): JSX.Element {
  return (
    <div className={css(styles.tabContentContainer)}>
      {props.selectedFieldId ? (
        <TransitionFieldInfoSection
          selectedFieldId={props.selectedFieldId}
          node={props.selectedNode}
          flowchart={props.flowchart}
          allSchemasById={props.allSchemasById}
          flowchartTaskSchema={props.flowchartTaskSchema}
          transitionSource={props.transitionSource}
          customMappingDataByEdgeId={props.customMappingDataByEdgeId}
          onChangeCustomMappingData={props.onChangeCustomMappingData}
        />
      ) : (
        <span className={css(styles.noFieldSelectedText)}>
          Select a field on the node to view its mappings
        </span>
      )}
    </div>
  );
}

// TODO(BNCH-122158): add onChangeFlowchart callback
export interface DefaultTaskSchemaNodeDetailsSidepanelContentProps extends NodeDetailsSidepanelContentProps {}

/**
 * Right sidepanel content for a default task schema node
 */
export default function DefaultTaskSchemaNodeDetailsSidepanelContent(
  props: DefaultTaskSchemaNodeDetailsSidepanelContentProps
): JSX.Element {
  const [activeTab, setActiveTab] = useState<NodeSettingsTabKeys>(
    props.selectedFieldId ? NodeSettingsTabKeys.NODE_FIELD_DETAILS : NodeSettingsTabKeys.FLOWCHART_SETTINGS
  );

  useEffect(() => {
    setActiveTab(
      props.selectedFieldId ? NodeSettingsTabKeys.NODE_FIELD_DETAILS : NodeSettingsTabKeys.FLOWCHART_SETTINGS
    );
  }, [props.selectedFieldId]);

  return (
    <TabContainer<NodeSettingsTabKeys>
      activeTab={activeTab}
      onRequestTabChange={setActiveTab}
      className={classNames(css(styles.sidepanelTabContainerStyle))}
      headerDividerClassName={css(styles.sidepanelHeaderDividerStyle)}
    >
      <Tab
        tabKey={NodeSettingsTabKeys.NODE_FIELD_DETAILS}
        title={<div className={css(styles.tabTitleStyle)}>Inputs & Outputs</div>}
      >
        <NodeFieldDetailsPane
          selectedNode={props.selectedNode}
          selectedFieldId={props.selectedFieldId}
          flowchart={props.flowchart}
          allSchemasById={props.allSchemasById}
          flowchartTaskSchema={props.flowchartTaskSchema}
          transitionSource={props.transitionSource}
          customMappingDataByEdgeId={props.customMappingDataByEdgeId}
          onChangeCustomMappingData={props.onChangeCustomMappingData}
        />
      </Tab>
      <Tab
        tabKey={NodeSettingsTabKeys.FLOWCHART_SETTINGS}
        title={<div className={css(styles.tabTitleStyle)}>Flowchart Settings</div>}
      >
        <FlowchartSettingsPane selectedNode={props.selectedNode} flowchart={props.flowchart} />
      </Tab>
    </TabContainer>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '16px',
    marginTop: '-23px',
  },
  tabTitleStyle: {
    display: 'flex',
    fontSize: '12px',
    paddingTop: '12px',
    paddingBottom: '6px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  sidepanelTabContainerStyle: {
    marginTop: '-4px',
  },
  sidepanelHeaderDividerStyle: {
    borderTop: `1px solid ${Color.GRAY_300}`,
  },
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
});
